// Import Swiper styles
import 'swiper/css'
import 'swiper/css/navigation'
import 'swiper/css/pagination'

import { useEffect, useRef, useState } from 'react'
import { A11y, Autoplay, Keyboard, Mousewheel, Navigation, Pagination } from 'swiper/modules'
import { Swiper, SwiperSlide } from 'swiper/react'
import type { Swiper as SwiperClass } from 'swiper/types'

import { PhnxImageHolder } from '@/components/01-atoms/ImageHolder/ImageHolder'
import { StylePanelProps } from '@/lib/stylePanel'

import { PhnxSectiontitle } from '../../01-atoms/Sectiontitle/Sectiontitle'
import styles from './Imageslider.module.css'

export type ImagesliderProps = {
  variant?: 'image' | 'endorsement' | 'article' | 'report'
  title?: string
  description?: string
  autoplay?: boolean
  autoplayDelay?: number
  items?:
    | {
        title?: string
        description?: string
        image?: {
          src: string
          alt: string
          focus?: string
        }
      }[]
    | React.ReactNode[]
} & StylePanelProps

export const PhnxImageslider: React.FC<ImagesliderProps> = ({
  variant = 'image',
  title,
  description,
  autoplay = true,
  autoplayDelay = 3000,
  items,
  backgroundColor = '#1b1f22',
  textColor = '#FFF',
}) => {
  const style = {
    backgroundColor,
    color: textColor,
  }

  const swiperRef = useRef<SwiperClass | null>(null)
  const [shouldLoop, setShouldLoop] = useState(true)

  // Function to get current slides per view based on window width and variant
  const getCurrentSlidesPerView = () => {
    if (typeof window === 'undefined') return 3

    const width = window.innerWidth

    if (variant === 'image') {
      if (width < 768) return 1
      if (width < 1024) return 2
      return 3
    } else {
      // For article and endorsement variants, use 'auto' sizing
      // Estimate based on container width and item widths
      if (variant === 'article') {
        const itemWidth = width >= 768 ? 294 : 315
        const spacing = 30
        const containerPadding = 48 // approximate
        const availableWidth = width - containerPadding
        return Math.floor(availableWidth / (itemWidth + spacing))
      } else if (variant === 'endorsement') {
        const itemWidth = width >= 768 ? 400 : 260
        const spacing = 30
        const containerPadding = 48 // approximate
        const availableWidth = width - containerPadding
        return Math.floor(availableWidth / (itemWidth + spacing))
      }
    }

    return 3
  }

  // Function to determine if looping should be enabled
  const calculateShouldLoop = () => {
    if (!items || items.length === 0) return false

    const currentSlidesPerView = getCurrentSlidesPerView()
    // Only enable loop if we have more items than can be displayed at once
    return items.length > currentSlidesPerView
  }

  // Update loop state when window resizes or items change
  useEffect(() => {
    const updateLoopState = () => {
      const newShouldLoop = calculateShouldLoop()

      setShouldLoop(newShouldLoop)

      // Update Swiper instance if it exists
      if (swiperRef.current) {
        swiperRef.current.params.loop = newShouldLoop
        swiperRef.current.params.loopAdditionalSlides = newShouldLoop ? 2 : 0
        swiperRef.current.params.slidesPerGroupSkip = newShouldLoop ? 1 : 0
        swiperRef.current.update()
      }
    }

    // Initial calculation
    updateLoopState()

    // Update on resize with debounce for better performance
    let resizeTimeout: ReturnType<typeof setTimeout>
    const handleResize = () => {
      clearTimeout(resizeTimeout)
      resizeTimeout = setTimeout(updateLoopState, 100)
    }

    window.addEventListener('resize', handleResize)
    return () => {
      window.removeEventListener('resize', handleResize)
      clearTimeout(resizeTimeout)
    }
  }, [items, variant])

  // Duplicate items if there are too few for smooth looping (only when loop is enabled)
  const getItemsForLoop = () => {
    if (items == null || items.length === 0) return []

    const itemsArray = Array.isArray(items) ? items : []

    // If looping is disabled, just return the original items
    if (!shouldLoop) {
      return itemsArray
    }

    const minItemsForLoop = 6 // Minimum items needed for smooth loop

    if (itemsArray.length >= minItemsForLoop) {
      return itemsArray
    }

    // Duplicate items until we have enough for smooth looping
    const duplicatedItems = []
    while (duplicatedItems.length < minItemsForLoop) {
      duplicatedItems.push(...itemsArray)
    }

    return duplicatedItems.slice(0, Math.max(minItemsForLoop, itemsArray.length * 2))
  }

  const loopItems = getItemsForLoop()
  const shouldShowSlider = items != null && items.length > 3

  // Function to equalize heights for both slider and static grid
  const equalizeSlideHeights = () => {
    if (variant !== 'article') return

    const cardContainers: Array<{ wrapper: HTMLElement; card: HTMLElement }> = []

    if (shouldShowSlider && swiperRef.current) {
      // Handle Swiper slides
      const slides = swiperRef.current.slides
      if (slides != null && slides.length > 0) {
        slides.forEach((slide) => {
          const slideElement = slide as HTMLElement
          const articleWrapper = slideElement.querySelector(
            `.${styles.articleWrapper}`
          ) as HTMLElement
          const card = slideElement.querySelector('[class*="phnxcard"]') as HTMLElement

          if (articleWrapper != null && card != null) {
            // Reset heights
            articleWrapper.style.height = 'auto'
            card.style.height = 'auto'
            cardContainers.push({ wrapper: articleWrapper, card })
          }
        })
      }
    } else {
      // Handle static grid
      const staticGrid = document.querySelector(`.${styles.staticGrid}`)
      if (staticGrid) {
        const articleWrappers = staticGrid.querySelectorAll(`.${styles.articleWrapper}`)
        articleWrappers.forEach((wrapper) => {
          const articleWrapper = wrapper as HTMLElement
          const card = wrapper.querySelector('[class*="phnxcard"]') as HTMLElement

          if (articleWrapper != null && card != null) {
            // Reset heights
            articleWrapper.style.height = 'auto'
            card.style.height = 'auto'
            cardContainers.push({ wrapper: articleWrapper, card })
          }
        })
      }
    }

    if (cardContainers.length === 0) return

    // Get the tallest card height after reset
    let maxHeight = 0
    cardContainers.forEach(({ card }) => {
      const height = card.offsetHeight
      if (height > maxHeight) {
        maxHeight = height
      }
    })

    // Apply max height to all cards and wrappers
    cardContainers.forEach(({ wrapper, card }) => {
      wrapper.style.height = `${maxHeight}px`
      card.style.height = `${maxHeight}px`
    })
  }

  useEffect(() => {
    // Equalize heights on mount and window resize
    const handleResize = () => {
      // Clear any existing timeout
      setTimeout(() => {
        equalizeSlideHeights()
      }, 150)
    }

    // Throttle resize events for better performance
    let resizeTimeout: ReturnType<typeof setTimeout>
    const throttledResize = () => {
      clearTimeout(resizeTimeout)
      resizeTimeout = setTimeout(handleResize, 100)
    }

    window.addEventListener('resize', throttledResize)

    // Initial equalization with multiple attempts to ensure DOM is ready
    const timeouts: ReturnType<typeof setTimeout>[] = []

    // Try multiple times with increasing delays to catch different render states
    timeouts.push(setTimeout(equalizeSlideHeights, 100))
    timeouts.push(setTimeout(equalizeSlideHeights, 500))
    timeouts.push(setTimeout(equalizeSlideHeights, 1000))
    timeouts.push(setTimeout(equalizeSlideHeights, 2000))

    return () => {
      window.removeEventListener('resize', throttledResize)
      clearTimeout(resizeTimeout)
      timeouts.forEach((timeout) => clearTimeout(timeout))
    }
  }, [variant, items, shouldShowSlider])

  return (
    <div style={style} className="stylepanel">
      <div className={`${styles.imageslider} ${styles[variant as keyof typeof styles]}`}>
        <div className={`container ${styles[`container${variant}` as keyof typeof styles]}`}>
          <div className={styles.content}>
            {title != null && title.length > 0 && (
              <div className={styles.title}>
                <PhnxSectiontitle title={title} />
              </div>
            )}
            {description != null && description.length > 0 && (
              <div className={styles.description}>
                <p>{description}</p>
              </div>
            )}
          </div>

          <div className={styles.slider}>
            {variant === 'report' && items && items.length > 0 ? (
              <Swiper
                onSwiper={(swiper: SwiperClass) => {
                  swiperRef.current = swiper
                }}
                slidesPerView={3} // 3 slides visible
                spaceBetween={30}
                loop={true} // disable loop
                mousewheel={true} // scroll both ways
                keyboard={{ enabled: true }}
                pagination={{
                  clickable: true,
                  type: 'bullets',
                  renderBullet: (index, className) => {
                    // Only render bullets for original items
                    if (index >= items.length) return ''
                    return `<span class="${className}"></span>`
                  },
                }}
                onSlideChange={(swiper) => {
                  // Correct the pagination when using loop
                  const realIndex = swiper.realIndex % (items?.length ?? 1)
                  const bullets = document.querySelectorAll(
                    `.${styles.mySwiper} .swiper-pagination-bullet`
                  )
                  bullets.forEach((bullet, idx) => {
                    bullet.classList.toggle('swiper-pagination-bullet-active', idx === realIndex)
                  })
                }}
                autoplay={
                  autoplay
                    ? { delay: autoplayDelay, disableOnInteraction: false, pauseOnMouseEnter: true }
                    : false
                }
                modules={[A11y, Keyboard, Mousewheel, Autoplay, Pagination]}
                className={styles.mySwiper}
                breakpoints={{
                  0: { slidesPerView: 1, spaceBetween: 20 },
                  768: { slidesPerView: 2, spaceBetween: 30 },
                  1024: { slidesPerView: 3, spaceBetween: 30 },
                }}
              >
                {loopItems.length > 0 &&
                  loopItems.map((item, index) => (
                    <SwiperSlide key={index} className={styles.mySlider}>
                      <div className={styles.slide}>
                        <div className={styles.imageContainer}>
                          {typeof item === 'object' &&
                            item != null &&
                            'image' in item &&
                            item.image && (
                              <PhnxImageHolder
                                images={[
                                  {
                                    src: item.image.src,
                                    alt: item.image.alt,
                                    focus: item.image.focus,
                                  },
                                ]}
                                maximumHeight={400}
                                maximumWidth={400}
                                imageStyle="square"
                              />
                            )}
                        </div>
                        <div className={styles.slideContent}>
                          {typeof item === 'object' &&
                            item != null &&
                            'title' in item &&
                            (item.title ?? '').length > 0 && (
                              <div className={styles.title}>
                                <h3>{item.title}</h3>
                              </div>
                            )}
                          {typeof item === 'object' &&
                            item != null &&
                            'description' in item &&
                            (item.description ?? '').length > 0 && (
                              <div className={styles.description}>{item.description}</div>
                            )}
                        </div>
                      </div>
                    </SwiperSlide>
                  ))}
              </Swiper>
            ) : shouldShowSlider ? (
              <>
                <Swiper
                  navigation={{
                    prevEl: `.${variant === 'article' ? styles.articlePrev : styles.endorsementPrev}`,
                    nextEl: `.${variant === 'article' ? styles.articleNext : styles.endorsementNext}`,
                  }}
                  onSwiper={(swiper: SwiperClass) => {
                    swiperRef.current = swiper
                  }}
                  onSlideChange={() => {
                    setTimeout(equalizeSlideHeights, 100)
                  }}
                  onAfterInit={() => {
                    setTimeout(equalizeSlideHeights, 200)
                  }}
                  onUpdate={() => {
                    setTimeout(equalizeSlideHeights, 100)
                  }}
                  slidesPerView={3}
                  spaceBetween={30}
                  watchSlidesProgress={true}
                  breakpoints={{
                    200: {
                      slidesPerView: variant === 'image' ? 1 : 'auto',
                      spaceBetween: 30,
                    },
                    320: {
                      slidesPerView: variant === 'image' ? 1 : 'auto',
                      spaceBetween: 30,
                    },
                    768: {
                      slidesPerView: variant === 'image' ? 2 : 'auto',
                      spaceBetween: 30,
                    },
                    1024: {
                      slidesPerView: variant === 'image' ? 3 : 'auto',
                      spaceBetween: 30,
                    },
                  }}
                  keyboard={{
                    enabled: true,
                  }}
                  mousewheel={true}
                  loop={shouldLoop}
                  loopAdditionalSlides={shouldLoop ? 2 : 0}
                  slidesPerGroupSkip={shouldLoop ? 1 : 0}
                  grabCursor={true}
                  autoplay={
                    autoplay === true
                      ? {
                          delay: autoplayDelay,
                          disableOnInteraction: false,
                          pauseOnMouseEnter: true,
                        }
                      : false
                  }
                  modules={[A11y, Keyboard, Mousewheel, Navigation, Autoplay]}
                  className={
                    variant === 'endorsement'
                      ? styles.myEndorsementSwiper
                      : variant === 'article'
                        ? styles.myArticleSwiper
                        : styles.mySwiper
                  }
                >
                  {loopItems.length > 0 &&
                    loopItems.map((item, index) => (
                      <SwiperSlide key={index} className={styles.mySlider}>
                        {variant === 'image' ? (
                          <div className={styles.slide}>
                            <div className={styles.imageContainer}>
                              {typeof item === 'object' &&
                                item != null &&
                                'image' in item &&
                                item.image && (
                                  <PhnxImageHolder
                                    images={[
                                      {
                                        src: item.image.src,
                                        alt: item.image.alt,
                                        focus: item.image.focus,
                                      },
                                    ]}
                                    maximumHeight={400}
                                    maximumWidth={400}
                                    imageStyle="square"
                                  />
                                )}
                            </div>
                            <div className={styles.slideContent}>
                              {typeof item === 'object' &&
                                item != null &&
                                'title' in item &&
                                (item.title ?? '').length > 0 && (
                                  <div className={styles.title}>
                                    <h3>{item.title}</h3>
                                  </div>
                                )}
                              {typeof item === 'object' &&
                                item != null &&
                                'description' in item &&
                                (item.description ?? '').length > 0 && (
                                  <div className={styles.description}>{item.description}</div>
                                )}
                            </div>
                          </div>
                        ) : variant === 'endorsement' ? (
                          <div className={styles.endorsementWrapper}>{item as React.ReactNode}</div>
                        ) : (
                          <div className={styles.articleWrapper}>{item as React.ReactNode}</div>
                        )}
                      </SwiperSlide>
                    ))}
                </Swiper>

                {shouldLoop && (
                  <>
                    <div
                      className={`swiper-button-prev ${variant === 'image' ? styles.prev : variant === 'article' ? styles.articlePrev : styles.endorsementPrev}`}
                    >
                      <span className={`fx-icon ${styles.icon}`}>
                        {variant === 'image' && (
                          <span className={`fx-icon fx-icon-arrow-left`}></span>
                        )}
                        <span className="sr-only">Previous</span>
                      </span>
                    </div>
                    <div
                      className={`swiper-button-next ${variant === 'image' ? styles.next : variant === 'article' ? styles.articleNext : styles.endorsementNext}`}
                    >
                      <span className={`fx-icon ${styles.icon}`}>
                        {variant === 'image' && (
                          <span className={`fx-icon fx-icon-arrow-down-lg`}></span>
                        )}
                        <span className="sr-only">Next</span>
                      </span>
                    </div>
                  </>
                )}
              </>
            ) : (
              // Static layout for 3 or fewer items
              <div className={styles.staticGrid}>
                {items != null &&
                  items.length > 0 &&
                  items.map((item, index) => (
                    <div key={index} className={styles.staticItem}>
                      {variant === 'image' ? (
                        <div className={styles.slide}>
                          <div className={styles.imageContainer}>
                            {typeof item === 'object' &&
                              item != null &&
                              'image' in item &&
                              item.image && (
                                <PhnxImageHolder
                                  images={[
                                    {
                                      src: item.image.src,
                                      alt: item.image.alt,
                                      focus: item.image.focus,
                                    },
                                  ]}
                                  maximumHeight={400}
                                  imageStyle="square"
                                />
                              )}
                          </div>
                          <div className={styles.slideContent}>
                            {typeof item === 'object' &&
                              item != null &&
                              'title' in item &&
                              (item.title ?? '').length > 0 && (
                                <div className={styles.title}>
                                  <h3>{item.title}</h3>
                                </div>
                              )}
                            {typeof item === 'object' &&
                              item != null &&
                              'description' in item &&
                              (item.description ?? '').length > 0 && (
                                <div className={styles.description}>{item.description}</div>
                              )}
                          </div>
                        </div>
                      ) : variant === 'endorsement' ? (
                        <div className={styles.endorsementWrapper}>{item as React.ReactNode}</div>
                      ) : (
                        <div className={styles.articleWrapper}>{item as React.ReactNode}</div>
                      )}
                    </div>
                  ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  )
}
